/**
 * Човен.
 *
 * **The boat is not a body.** It carries one, and the one it carries is the
 * player - who already has a collider, a camera and a set of controls. So a
 * boat with its own physics would mean two things standing in the same place
 * arguing about where that place is, which is the shape of every bug this
 * sort of feature has. Instead: while somebody is in it, the boat *is* where
 * they are, and the ordinary walking physics is not run at all.
 *
 * What that leaves is small and worth having on its own: where a boat floats,
 * which way it is pointing, and whether the water carries on in the direction
 * somebody is trying to go. Pure - no world, no renderer, no input device.
 */

/** How fast a човен moves under oars, in blocks a second. */
export const BOAT_SPEED = 5.2;
/** And how fast it comes round, in radians a second. */
export const BOAT_TURN = 2.2;
/**
 * How quickly it gathers way and loses it.
 *
 * A boat that starts and stops like a pair of boots is a pair of boots with
 * a hull drawn round them. This is the whole of what makes it feel like a
 * boat, and it is one number.
 */
export const BOAT_DRAG = 1.6;

/**
 * Where the floor of the boat sits, relative to the top of the water.
 *
 * The boat is placed by its floor rather than by its middle, because that is
 * where the person standing in it has their feet - so it is small, and it
 * is *below* the waterline: a flat-bottomed довбанка floats with its bottom
 * boards in the water and the inside of it barely above, which is why you
 * bail one out. Set to a third of a block, as it was for one draft, and the
 * boat sits on the lake like a tray on ice.
 */
export const BOAT_DRAFT = -0.06;

export interface Boat {
  readonly id: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Speed along its own heading. Negative is backing water. */
  way: number;
  /** Who is in it, or null. */
  rider: number | null;
}

let nextId = 1;

export function makeBoat(at: { x: number; y: number; z: number }, yaw = 0): Boat {
  return { id: nextId++, x: at.x, y: at.y, z: at.z, yaw, way: 0, rider: null };
}

/**
 * One the server owns, drawn here.
 *
 * Built rather than made-and-renumbered, the same as a mirrored beast: the
 * maker takes the next local id as it goes, so mirroring somebody else's
 * boat would push this page's own numbering along for a hull it never made -
 * and then the next boat *we* launched would collide with one of theirs.
 */
export function mirrorBoat(
  id: number, at: { x: number; y: number; z: number }, yaw: number,
): Boat {
  return { id, x: at.x, y: at.y, z: at.z, yaw, way: 0, rider: null };
}

/** What a boat needs to know about the world it is on. */
export interface Water {
  /** The top of the water in this column, or null if there is none. */
  surfaceAt(x: number, z: number): number | null;
}

/** Which way somebody at the oars is pulling. */
export interface Oars {
  readonly forward: boolean;
  readonly back: boolean;
  readonly left: boolean;
  readonly right: boolean;
}

export const STILL: Oars = {
  forward: false, back: false, left: false, right: false,
};

/**
 * Pull on the oars for `dt` seconds.
 *
 * The heading turns whether or not the boat is moving, which is a rowing
 * boat and not a car: you can spin one on the spot with one oar, and a boat
 * that only turned while under way would be unusable in the reeds.
 *
 * Where it *goes* is decided by the water and not by the boat: a cell with
 * no water in it is a bank, and the boat stops against it rather than
 * sliding up the beach. That is also what keeps the thing honest without a
 * collider - there is nothing to collide with, because the only place it is
 * ever allowed to be is on water.
 *
 * @returns whether it actually moved, so the caller can make a noise.
 */
export function rowBoat(
  boat: Boat, oars: Oars, dt: number, water: Water,
): boolean {
  if (oars.left) boat.yaw += BOAT_TURN * dt;
  if (oars.right) boat.yaw -= BOAT_TURN * dt;

  const want = (oars.forward ? 1 : 0) - (oars.back ? 0.45 : 0);
  if (want !== 0) {
    boat.way += (want * BOAT_SPEED - boat.way) * Math.min(1, BOAT_DRAG * dt);
  } else {
    boat.way -= boat.way * Math.min(1, BOAT_DRAG * dt);
    if (Math.abs(boat.way) < 0.02) boat.way = 0;
  }
  if (boat.way === 0) {
    const here = water.surfaceAt(boat.x, boat.z);
    if (here !== null) boat.y = here + BOAT_DRAFT;
    return false;
  }

  const nx = boat.x + Math.sin(boat.yaw) * boat.way * dt;
  const nz = boat.z + Math.cos(boat.yaw) * boat.way * dt;
  const ahead = water.surfaceAt(nx, nz);
  if (ahead === null) {
    // A bank. Stop dead rather than drifting into it for the next second.
    boat.way = 0;
    const here = water.surfaceAt(boat.x, boat.z);
    if (here !== null) boat.y = here + BOAT_DRAFT;
    return false;
  }
  boat.x = nx;
  boat.z = nz;
  boat.y = ahead + BOAT_DRAFT;
  return true;
}

/**
 * Somewhere to step out onto, next to the boat.
 *
 * Getting out into the water would be getting out into the water, which is
 * not what anybody means by getting out of a boat. So the eight cells round
 * it are asked for dry footing and the nearest one wins; with none, the
 * answer is no and the player stays aboard.
 */
export function stepAshore(
  boat: Boat,
  footing: (x: number, z: number) => number | null,
): { x: number; y: number; z: number } | null {
  const around: readonly (readonly [number, number])[] = [
    [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
  ];
  let best: { x: number; y: number; z: number } | null = null;
  let nearest = Infinity;
  for (const [dx, dz] of around) {
    const x = Math.floor(boat.x) + dx;
    const z = Math.floor(boat.z) + dz;
    const y = footing(x, z);
    if (y === null) continue;
    const away = Math.abs(y - boat.y) + Math.hypot(dx, dz);
    if (away < nearest) { nearest = away; best = { x: x + 0.5, y, z: z + 0.5 }; }
  }
  return best;
}
